import {
  type DeviceDefinitionDto,
  type DiagnosticDefinitionDto,
  type DiagnosticValueDefinitionDto,
  displayAnsweredValue,
  effectiveParameters,
  type TreatmentDefinitionDto,
  type TreatmentValueDefinitionDto,
} from "@repo/data-store";
import type { InvalidatedValue } from "@repo/domain";
import type { RedesignedProtocolContent } from "@/features/catalog/forms/use-plan-definition-form";
import type { ActivityParameter } from "@/shared/lib/template-utils";

export type ProtocolBuilderParameterDefinition = DiagnosticValueDefinitionDto | TreatmentValueDefinitionDto;

export interface ProtocolBuilderParameter {
  source: "self" | string;
  /** The required device definition's name; absent for the procedure's own parameters. */
  sourceLabel?: string;
  parameterId: string;
  label: string;
  value: ProtocolBuilderParameterDefinition;
}

/** Neutral procedure declaration consumed by the shared drag/drop builder. */
export interface ProtocolBuilderProcedure {
  model: "legacy" | "redesigned";
  resourceId: string;
  canonical: string;
  title: string;
  description?: string;
  kind: "Diagnostic" | "Treatment";
  availability?: "Active" | "Inactive";
  legacyParameters: ActivityParameter[];
  domainParameters: ProtocolBuilderParameter[];
  observations?: readonly { id: string; label: string }[];
  hasResourceRequirements: boolean;
  /** Device requirements whose definition has not loaded, so their parameters are not yet known. */
  unresolvedSources?: readonly string[];
}

export interface ProtocolBuilderParameterBadge {
  label: string;
  display: string;
}

export type ProtocolBuilderDomainAnswer = {
  source: "self" | string;
  parameterId: string;
  value:
    | { kind: "Quantity"; value: number; unit?: { code: string; display: string } }
    | { kind: "Integer"; value: number }
    | { kind: "Choice"; selected: string[]; freeText?: string }
    | { kind: "Boolean"; value: boolean }
    | { kind: "Text"; value: string };
};

export type ProtocolBuilderPlannedTrial = { id: string; values: ProtocolBuilderDomainAnswer[] };

export function protocolBuilderParameterBadges(
  definition: ProtocolBuilderProcedure,
  values: readonly ({ name: string; display: string } | ProtocolBuilderDisplayAnswer)[],
): ProtocolBuilderParameterBadge[] {
  const legacyLabels = new Map(definition.legacyParameters.map((parameter) => [parameter.name, parameter.text]));
  const domainParameters = new Map(
    definition.domainParameters.map((parameter) => [answerKey(parameter.source, parameter.parameterId), parameter]),
  );
  return values.flatMap((answer) => {
    if ("name" in answer) return [{ label: legacyLabels.get(answer.name) ?? answer.name, display: answer.display }];
    const parameter = domainParameters.get(answerKey(answer.source, answer.parameterId));
    if (!parameter) return [];
    return [{ label: parameter.label, display: displayAnsweredValue(parameter.value, answer.value) }];
  });
}

function answerKey(source: string, parameterId: string): string {
  return `${source}\0${parameterId}`;
}

type ProtocolBuilderDisplayAnswer = Omit<ProtocolBuilderDomainAnswer, "value"> & {
  value:
    | Exclude<ProtocolBuilderDomainAnswer["value"], { kind: "Choice" }>
    | {
        kind: "Choice";
        selected: readonly string[];
        freeText?: string;
      };
};

export function findProtocolBuilderProcedure(
  canonical: string | undefined,
  definitions: readonly ProtocolBuilderProcedure[],
): ProtocolBuilderProcedure | undefined {
  if (!canonical) return undefined;
  return definitions.find(
    (definition) =>
      definition.canonical === canonical ||
      `ActivityDefinition/${definition.resourceId}` === canonical ||
      definition.resourceId === canonical,
  );
}

/**
 * Presentation-only composition of the two concrete catalog definition views.
 *
 * `deviceDefinitions` is every definition, active or not: a requirement is unpinned, so an
 * authored protocol can name a device definition that has since been deactivated, and its
 * parameters still have to be answerable.
 */
export function protocolBuilderProcedures(
  diagnostics: readonly DiagnosticDefinitionDto[],
  treatments: readonly TreatmentDefinitionDto[],
  deviceDefinitions: readonly DeviceDefinitionDto[] = [],
): ProtocolBuilderProcedure[] {
  const devicesByUrl = new Map(deviceDefinitions.map((definition) => [definition.definitionUrl, definition]));
  return [
    ...diagnostics.map(
      (definition): ProtocolBuilderProcedure => ({
        model: "redesigned",
        resourceId: definition.resourceId ?? definition.definitionUrl,
        canonical: definition.definitionUrl,
        title: definition.name,
        ...(definition.description ? { description: definition.description } : {}),
        kind: "Diagnostic",
        observations: definition.chartedObservations.map(({ id, label }) => ({ id, label })),
        availability: definition.availability,
        legacyParameters: [],
        domainParameters: effectiveParameters<ProtocolBuilderParameterDefinition>(
          definition.effectiveParameters,
          definition.requirements.devices,
          devicesByUrl,
        ),
        ...unresolved(
          definition.requirements.devices
            .filter((requirement) => !devicesByUrl.has(requirement.definitionUrl))
            .map((requirement) => requirement.id),
        ),
        hasResourceRequirements: definition.requirements.devices.length > 0 || !!definition.requirements.locationType,
      }),
    ),
    ...treatments.map(
      (definition): ProtocolBuilderProcedure => ({
        model: "redesigned",
        resourceId: definition.resourceId ?? definition.url,
        canonical: definition.url,
        title: definition.name,
        ...(definition.description ? { description: definition.description } : {}),
        kind: "Treatment",
        availability: definition.availability,
        legacyParameters: [],
        domainParameters: effectiveParameters<ProtocolBuilderParameterDefinition>(
          definition.effectiveParameters,
          definition.deviceRequirements.map((requirement) => ({
            id: requirement.id,
            definitionUrl: requirement.definition,
          })),
          devicesByUrl,
        ),
        ...unresolved(
          definition.deviceRequirements
            .filter((requirement) => !devicesByUrl.has(requirement.definition))
            .map((requirement) => requirement.id),
        ),
        hasResourceRequirements: definition.deviceRequirements.length > 0 || !!definition.locationRequirement,
      }),
    ),
  ];
}

function unresolved(sources: readonly string[]): { unresolvedSources?: readonly string[] } {
  return sources.length ? { unresolvedSources: sources } : {};
}

/**
 * The builder never shows a value whose parameter the procedure no longer
 * declares, so saving sends only what it shows. Values under a device that has
 * not loaded yet are kept: their parameters are unknown, not gone.
 */
export function withoutOrphanedValues(
  content: RedesignedProtocolContent,
  procedures: readonly ProtocolBuilderProcedure[],
): RedesignedProtocolContent {
  const byCanonical = new Map(procedures.map((procedure) => [procedure.canonical, procedure]));
  return {
    ...content,
    steps: content.steps.map((step) => ({
      ...step,
      occurrences: step.occurrences.map((occurrence) => {
        const procedure = byCanonical.get(occurrence.definition);
        if (!procedure) return occurrence;
        const kept = <T extends { source: string; parameterId: string }>(answers: readonly T[]) =>
          answers.filter(
            (answer) =>
              procedure.unresolvedSources?.includes(answer.source) ||
              procedure.domainParameters.some(
                (parameter) => parameter.source === answer.source && parameter.parameterId === answer.parameterId,
              ),
          );
        return {
          ...occurrence,
          parameterValues: kept(occurrence.parameterValues),
          ...(occurrence.plannedTrials
            ? { plannedTrials: occurrence.plannedTrials.map((trial) => ({ ...trial, values: kept(trial.values) })) }
            : {}),
        };
      }),
    })),
  };
}

/** The content without the named values, which a blocked save chose to remove. */
export function withoutValues(
  content: RedesignedProtocolContent,
  values: readonly InvalidatedValue[],
): RedesignedProtocolContent {
  if (!values.length) return content;
  const named = (
    occurrenceId: string | undefined,
    plannedTrialId: string | undefined,
    answer: { source: string; parameterId: string },
  ) =>
    values.some(
      (value) =>
        value.occurrenceId === occurrenceId &&
        value.plannedTrialId === plannedTrialId &&
        value.source === answer.source &&
        value.parameterId === answer.parameterId,
    );
  return {
    ...content,
    steps: content.steps.map((step) => ({
      ...step,
      occurrences: step.occurrences.map((occurrence) => ({
        ...occurrence,
        parameterValues: occurrence.parameterValues.filter((answer) => !named(occurrence.id, undefined, answer)),
        ...(occurrence.plannedTrials
          ? {
              plannedTrials: occurrence.plannedTrials.map((trial) => ({
                ...trial,
                values: trial.values.filter((answer) => !named(occurrence.id, trial.id, answer)),
              })),
            }
          : {}),
      })),
    })),
  };
}
